import React, { useState, useEffect } from 'react';
import LocationDropdown from '../../Dropdown/LocationName';
import AreaDropdown from '../../Dropdown/AreaDropdown';

const AssetsAddModal = ({
  show,
  onClose,
  formData,
  onChange,
  onSave,
  isEditMode,
  isLoading,
}) => {
  // State to track which fields have been validated (shows error if empty)
  const [validatedFields, setValidatedFields] = useState({
    assets_name: false,
    total_quantity: false,
    assets_limit: false,
    status: false,
    location_id: false,
    area_id: false,
    asset_category: false,
    asset_type: formData.asset_category !== 'Device' // Only validate asset_type if category is Device
  });

  // State for quantity validation errors
  const [quantityError, setQuantityError] = useState('');
  const [quantityZeroError, setQuantityZeroError] = useState('');

  // Effect to validate quantity against limit and check for zero value
  useEffect(() => {
    if (formData.total_quantity) {
      const quantity = parseInt(formData.total_quantity);
      
      // Validate quantity is greater than 0
      if (quantity <= 0) {
        setQuantityZeroError('Quantity must be greater than 0');
      } else {
        setQuantityZeroError('');
      }

      // Validate quantity against limit if limit exists
      if (formData.assets_limit) {
        const limit = parseInt(formData.assets_limit);
        if (quantity > limit) {
          setQuantityError(`Quantity (${quantity}) cannot be greater than limit (${limit})`);
        } else {
          setQuantityError('');
        }
      }
    } else {
      setQuantityError('');
      setQuantityZeroError('');
    }
  }, [formData.total_quantity, formData.assets_limit]);

  // Form submission handler
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mark all fields as validated when submitting
    const allFieldsValidated = {
      assets_name: true,
      total_quantity: true,
      assets_limit: true,
      status: true,
      location_id: true,
      area_id: true,
      asset_category: true,
      asset_type: formData.asset_category === 'Device' // Only validate if category is Device
    };
    setValidatedFields(allFieldsValidated);

    // Check if all required fields are filled and validations pass
    const isFormValid = Object.keys(allFieldsValidated).every(
      field => !allFieldsValidated[field] || !!formData[field]
    ) && !quantityError && !quantityZeroError;

    if (isFormValid) {
      onSave();
    }
  };

  // Handler for field blur events - marks field as validated
  const handleBlur = (fieldName) => {
    if (!validatedFields[fieldName]) {
      setValidatedFields(prev => ({
        ...prev,
        [fieldName]: true
      }));
    }
  };

  // Helper to determine if validation message should be shown for a field
  const shouldShowValidation = (fieldName) => {
    return validatedFields[fieldName] && !formData[fieldName];
  };

  // Don't render if modal is not shown
  if (!show) return null;

  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-scrollable modal-lg">
        <div className="modal-content">
          <div className="modal-header d-flex align-items-center">
            <h5 className="modal-title">
              {isEditMode ? 'Edit Asset' : 'Add Asset'}
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-12">
                  <div className="mb-3">
                    <label>Asset Name</label>
                    <input
                      type="text"
                      className={`form-control ${shouldShowValidation('assets_name') ? 'is-invalid' : ''}`}
                      name="assets_name"
                      value={formData.assets_name || ''}
                      onChange={onChange}
                      onBlur={() => handleBlur('assets_name')}
                      required
                    />
                    {shouldShowValidation('assets_name') && (
                      <div className="invalid-feedback">Asset name is required</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Quantity</label>
                    <input
                      type="number"
                      className={`form-control ${
                        shouldShowValidation('total_quantity') || 
                        quantityError || 
                        quantityZeroError ? 'is-invalid' : ''
                      }`}
                      name="total_quantity"
                      value={formData.total_quantity || ''}
                      onChange={onChange}
                      onBlur={() => handleBlur('total_quantity')}
                      min="1"
                      required
                    />
                    {shouldShowValidation('total_quantity') && (
                      <div className="invalid-feedback">Quantity is required</div>
                    )}
                    {quantityZeroError && (
                      <div className="invalid-feedback">{quantityZeroError}</div>
                    )}
                    {quantityError && !quantityZeroError && (
                      <div className="invalid-feedback">{quantityError}</div>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Limit</label>
                    <input
                      type="number"
                      className={`form-control ${shouldShowValidation('assets_limit') ? 'is-invalid' : ''}`}
                      name="assets_limit"
                      value={formData.assets_limit || ''}
                      onChange={onChange}
                      onBlur={() => handleBlur('assets_limit')}
                      min="1"
                      required
                    />
                    {shouldShowValidation('assets_limit') && (
                      <div className="invalid-feedback">Limit is required</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Rest of the form fields */}
              <div className="row">
                <div className="col-md-12">
                  <div className="mb-3">
                    <label>Available in Organisation</label>
                    <select
                      className={`form-control form-select ${shouldShowValidation('status') ? 'is-invalid' : ''}`}
                      name="status"
                      value={formData.status ? 'true' : 'false'}
                      onChange={(e) =>
                        onChange({
                          target: {
                            name: 'status',
                            value: e.target.value === 'true',
                          },
                        })
                      }
                      onBlur={() => handleBlur('status')}
                      required
                    >
                      <option value="">Select Availability</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                    {shouldShowValidation('status') && (
                      <div className="invalid-feedback">Please select availability</div>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Location</label>
                    <LocationDropdown
                      value={formData.location_id}
                      onChange={onChange}
                      onBlur={() => handleBlur('location_id')}
                    />
                    {shouldShowValidation('location_id') && (
                      <div className="invalid-feedback">Location is required</div>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Area</label>
                    <AreaDropdown
                      locationId={formData.location_id}
                      value={formData.area_id}
                      onChange={onChange}
                      onBlur={() => handleBlur('area_id')}
                    />
                    {shouldShowValidation('area_id') && (
                      <div className="invalid-feedback">Area is required</div>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Asset Category</label>
                    <select
                      className={`form-control form-select ${shouldShowValidation('asset_category') ? 'is-invalid' : ''}`}
                      name="asset_category"
                      value={formData.asset_category || ''}
                      onChange={onChange}
                      onBlur={() => handleBlur('asset_category')}
                      required
                    >
                      <option value="">Select Category</option>
                      <option value="Device">Device</option>
                      <option value="Consumables">Consumables</option>
                    </select>
                    {shouldShowValidation('asset_category') && (
                      <div className="invalid-feedback">Category is required</div>
                    )}
                  </div>
                </div>

                {/* Conditionally render asset type field if category is Device */}
                {formData.asset_category === 'Device' && (
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label>Asset Type</label>
                      <select
                        className={`form-control form-select ${shouldShowValidation('asset_type') ? 'is-invalid' : ''}`}
                        name="asset_type"
                        value={formData.asset_type || ''}
                        onChange={onChange}
                        onBlur={() => handleBlur('asset_type')}
                        required
                      >
                        <option value="">Select Type</option>
                        <option value="1">One-to-one</option>
                        <option value="5">One-to-five</option>
                        <option value="10">One-to-ten</option>
                        <option value="0">Other</option>
                      </select>
                      {shouldShowValidation('asset_type') && (
                        <div className="invalid-feedback">Type is required for devices</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn mb-1 waves-effect waves-light btn-outline-danger"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn mb-1 waves-effect waves-light btn-primary"
                disabled={isLoading || quantityError || quantityZeroError}
              >
                {isLoading ? 'Saving...' : isEditMode ? 'Update' : 'Save Asset'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AssetsAddModal;